import { useEffect, useRef } from 'react'
import { useRouter } from 'expo-router'
import { toastNotificationStore } from '@/components/ANewDesign/ToastNotification/ToastNotification'
import { openMyTable } from '../mttNavigation'

/** Delay between the "you're being seated" toast and the auto-navigation. */
const AUTO_SEAT_DELAY_MS = 2000

type Params = {
  tournamentId: string | undefined
  lobbyState: string | null | undefined
  /** From `useMyTournamentTable`: non-null once the server has seated the user. */
  gameId: string | null | undefined
  tableNumber: number | null | undefined
}

/**
 * Auto-seat registered players when the tournament starts.
 *
 * At start the server creates tables and player rows in one mutation, so a
 * player watching the lobby sees `lobby.state` flip to 'active' and
 * `myTable.gameId` appear in the same push. Nothing navigated them to their
 * seat, leaving them in the lobby until they tapped "Go to Table".
 *
 * This hook mirrors the mid-tournament table-move UX: toast a heads-up
 * ("You're seated at Table N"), then auto-navigate to the table after a
 * short delay so the toast is readable.
 *
 * Fires in two cases:
 *  1. Tournament start: this lobby session observed the pre-start
 *     (`registration`) state, then the user got seated at start.
 *  2. Late-join / waitlist: this lobby session observed the user unseated
 *     (gameId null), then the server seated them (immediate seat or a
 *     waitlist seat freed by a bust-out).
 * A user who opens the lobby of an already-running tournament already seated
 * (to check chat / leaderboard) never sees a null-to-set transition and is
 * left alone.
 */
export function useAutoSeatOnStart({ tournamentId, lobbyState, gameId, tableNumber }: Params) {
  const router = useRouter()
  // All refs are keyed by tournamentId, so a different tournament's lobby
  // mounting on this screen never inherits stale state (no reset effect).
  const observedPreStartForRef = useRef<string | null>(null)
  // The toast and the navigation are guarded separately. A dependency change
  // inside the delay clears the pending timer, so the effect must be able to
  // schedule it again (without re-toasting); "done" is only set once the
  // navigation has actually run.
  const toastShownForRef = useRef<string | null>(null)
  const navigatedForRef = useRef<string | null>(null)
  // Fixed when the toast shows, so a rescheduled timer keeps the original
  // deadline instead of restarting the delay on every dependency change.
  const navigateAtRef = useRef(0)

  useEffect(() => {
    if (lobbyState === 'registration') observedPreStartForRef.current = tournamentId ?? null
  }, [tournamentId, lobbyState])

  // Track "was unseated in this mounted session" separately from gameId so the
  // first render with a seat already assigned (returning to a running
  // tournament) does NOT count as a transition.
  const observedUnseatedForRef = useRef<string | null>(null)
  useEffect(() => {
    // Strict null check: the lobby passes undefined while getMyTournamentTable
    // is still loading, so a resolving query never reads as "unseated".
    if (gameId === null) observedUnseatedForRef.current = tournamentId ?? null
  }, [tournamentId, gameId])

  useEffect(() => {
    if (!(tournamentId && gameId)) return
    if (lobbyState !== 'active') return
    const startedWhileWatching = observedPreStartForRef.current === tournamentId
    const seatedWhileWatching = observedUnseatedForRef.current === tournamentId
    if (!(startedWhileWatching || seatedWhileWatching)) return
    if (navigatedForRef.current === tournamentId) return

    if (toastShownForRef.current !== tournamentId) {
      toastShownForRef.current = tournamentId
      navigateAtRef.current = Date.now() + AUTO_SEAT_DELAY_MS
      const text = tableNumber ? `You're seated at Table ${tableNumber}.` : "You've been seated."
      toastNotificationStore.getState().show({ text })
    }

    const timeout = setTimeout(
      () => {
        navigatedForRef.current = tournamentId
        openMyTable(router, gameId)
      },
      Math.max(0, navigateAtRef.current - Date.now())
    )
    return () => clearTimeout(timeout)
  }, [tournamentId, lobbyState, gameId, tableNumber, router])
}
